/**
 * useAdminNavigation — Mini-Router fuer den Admin-Host (P0, docs/KONZEPT-ADMIN-BEREICH.md §
 * "Architektur-Entscheidung" Punkt 2, docs/LOOP-ADMIN-BEREICH.md § P0).
 *
 * WARUM KEIN `<Route>` (react-router): `apps/web/test/e2e-abdeckung.test.ts` liest woertlich nur
 * `<Route path="...">`-Tags aus `App.tsx` aus und verlangt fuer jede so gefundene NEUE Seiten-
 * Route sofort einen eigenen Playwright-Golden-Path-Spec (kein Ausnahme-Eintrag moeglich, siehe
 * `apps/web/test/e2e-abdeckung.ts` Kopfkommentar). `App.tsx` rendert auf dem Admin-Host
 * (`istAdminHost()`) unabhaengig vom Pfad ohnehin immer `AdminIndexPage` (siehe dortiger
 * `istAdminHost`-Zweig) — ein React-Router-`<Route>`-Baum wuerde diese Ratsche sofort scharf
 * schalten, obwohl das eigene Admin-E2E-Setup (Platform-Admin-Login + `maestro.`-Host) erst in P8
 * geplant ist. Dieser Hook parst Pfad + Query deshalb selbst und navigiert per
 * `history.pushState`/`popstate` — reine Client-Logik, keine Route-Registrierung, keine neue
 * Zeile fuer die Ratsche.
 *
 * Seitenbaum exakt aus `docs/KONZEPT-ADMIN-BEREICH.md` § "Informationsarchitektur & UI":
 *   /                Dashboard
 *   /mandanten       Liste
 *   /mandanten/neu   Onboarding-Assistent
 *   /mandanten/:id   Kontrollzentrum, 8 Tabs (?tab=)
 *   /impersonationen Aktive Grants + Historie
 *   /audit           Plattformweites Audit-Log
 *   /admins          Platform-Admin-Verwaltung
 *
 * Diese Iteration liefert NUR den Hook + seinen Test. `AdminShell.tsx`/`pages/admin/index.tsx`
 * bleiben bewusst unangetastet (State-Navigation via `selectedTenantId`) — die Verdrahtung ist ein
 * spaeterer P0-Schritt, sobald `TabBar`/`Select`/Toast (naechstes Kriterium) stehen.
 */
import { useCallback, useEffect, useState } from "react";

export type AdminSeite =
  | "dashboard"
  | "mandanten"
  | "mandanten-neu"
  | "mandanten-detail"
  | "impersonationen"
  | "audit"
  | "admins"
  /** Kein Treffer im Seitenbaum — Aufrufer entscheidet, was hier erscheint (z. B. dieselbe
   *  neutrale "Nicht gefunden"-Flaeche wie beim `whoami`-Existenz-Orakel, siehe `pages/admin/
   *  index.tsx`). Der Hook selbst trifft keine Auth-/Existenz-Entscheidung. */
  | "unbekannt";

export interface AdminRoute {
  seite: AdminSeite;
  /** Nur bei `seite === "mandanten-detail"` gesetzt. */
  tenantId?: string;
  /** Rohwert von `?tab=`, falls vorhanden — unabhaengig von `seite` durchgereicht (der Hook
   *  kennt die 8 gueltigen Tab-Namen der Detailseite nicht, das entscheidet der Tab-Verbraucher). */
  tab?: string;
}

/** Segmente ohne fuehrenden/nachgestellten Schraegstrich, leere Segmente (Doppel-Slash) raus. */
function segmente(pathname: string): string[] {
  return pathname.split("/").filter((s) => s.length > 0);
}

/** Reine Parse-Funktion, unabhaengig vom Hook testbar: Pfad + Query -> strukturierte Route. */
export function parseAdminPfad(pathname: string, search: string): AdminRoute {
  const teile = segmente(pathname);
  const tab = new URLSearchParams(search).get("tab") ?? undefined;

  if (teile.length === 0) return { seite: "dashboard", tab };
  if (teile[0] === "mandanten") {
    if (teile.length === 1) return { seite: "mandanten", tab };
    if (teile.length === 2 && teile[1] === "neu") return { seite: "mandanten-neu", tab };
    if (teile.length === 2) return { seite: "mandanten-detail", tenantId: teile[1], tab };
    return { seite: "unbekannt", tab };
  }
  if (teile.length === 1 && teile[0] === "impersonationen") return { seite: "impersonationen", tab };
  if (teile.length === 1 && teile[0] === "audit") return { seite: "audit", tab };
  if (teile.length === 1 && teile[0] === "admins") return { seite: "admins", tab };
  return { seite: "unbekannt", tab };
}

function aktuelleRoute(): AdminRoute {
  if (typeof window === "undefined") return { seite: "dashboard" };
  return parseAdminPfad(window.location.pathname, window.location.search);
}

export interface UseAdminNavigationApi {
  route: AdminRoute;
  /** Navigiert per `history.pushState` (kein Browser-Reload) und aktualisiert `route` sofort —
   *  `pushState` selbst loest KEIN `popstate` aus, das muss der Aufrufer hier nachbilden. `pfad`
   *  ist der volle Ziel-Pfad inkl. optionaler Query, z. B. `/mandanten/<id>?tab=einstellungen`. */
  navigiereZu: (pfad: string) => void;
}

export function useAdminNavigation(): UseAdminNavigationApi {
  const [route, setRoute] = useState<AdminRoute>(aktuelleRoute);

  useEffect(() => {
    const onPopState = () => setRoute(aktuelleRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigiereZu = useCallback((pfad: string) => {
    if (typeof window === "undefined") return;
    const aktuell = window.location.pathname + window.location.search;
    if (aktuell === pfad) return; // No-op: kein doppelter History-Eintrag fuer denselben Zustand
    window.history.pushState({}, "", pfad);
    setRoute(aktuelleRoute());
  }, []);

  return { route, navigiereZu };
}
